// Shared constants of the CTC assistant. Sites, product analysis, RMR drafts,
// Affilizz contents and opportunities are real; the article is generated and
// sent to the CMS by AifPostGeneration with the Gary template of its format.

export const VIEW_CREATE = "create";
export const VIEW_ARTICLES = "articles";
export const VIEW_OPPORTUNITIES = "opportunities";
export const VIEW_CONFIGURATION = "configuration";
export const VIEWS = [
  VIEW_CREATE,
  VIEW_ARTICLES,
  VIEW_OPPORTUNITIES,
  VIEW_CONFIGURATION,
];
export const VIEW_QUERY_PARAM = "view";

// Phase 1: steps 1–2 (product, widget, Affilizz affiliation).
// Phase 2: steps 3–4 (editorial settings, then launch).
export const CREATE_STEPS = [
  "Choisir le site et le produit",
  "Widget et affiliation Affilizz",
  "Paramètres de l’article",
  "Vérifier et lancer",
];
export const VERIFICATION_STEP = 2;

export const AUTHOR_LABEL = "L’équipe Shopping et bons plans";
export const SIGNATURE_MAX_LENGTH = 255;

export const TITLE_MAX_LENGTH = 200;

export const MERCHANTS = {
  amazon: { label: "Amazon", domain: "amazon.fr" },
  fnac: { label: "Fnac", domain: "fnac.com" },
};

export const AVAILABILITY_LABELS = {
  in_stock: "En stock",
  out_of_stock: "Rupture de stock",
  preorder: "Précommande",
  other_sellers: "Disponible auprès d’autres vendeurs",
  unknown: "Non communiquée",
};

// Where a product field comes from. Colors follow the RMR tokens.
export const PROVENANCES = {
  merchant_page: {
    label: "Page marchande",
    color: "#0284C7",
    background: "#F0F9FF",
  },
  affilizz: {
    label: "Affilizz",
    color: "#7C3AED",
    background: "#F5F3FF",
  },
  manual: {
    label: "Modifié",
    color: "#B45309",
    background: "#FFF7ED",
  },
};

// Same choices and wording as the other AI Factory flows
// (SendToPostGenerationQueue): "future" carries the scheduled date.
export const POST_STATUS_OPTIONS = [
  { value: "draft", label: "Brouillon" },
  { value: "publish", label: "Publier" },
  { value: "future", label: "Programmer" },
];

// Gary's two templates, common to every site (backend values).
export const EDITORIAL_FORMAT_FOCUS = "focus_product";
export const EDITORIAL_FORMAT_DEAL = "deal";
export const EDITORIAL_FORMAT_OPTIONS = [
  { value: EDITORIAL_FORMAT_FOCUS, label: "Focus produit" },
  { value: EDITORIAL_FORMAT_DEAL, label: "Bon plan" },
];
export const EDITORIAL_FORMAT_LABELS = {
  [EDITORIAL_FORMAT_FOCUS]: "Focus produit",
  [EDITORIAL_FORMAT_DEAL]: "Bon plan",
};
// Same wording as the backend refusal (deal_prices_invalid).
export const DEAL_PRICES_MESSAGE =
  "Le format Bon plan nécessite un prix actuel et un prix de comparaison supérieur. Complétez les prix ou sélectionnez Focus produit.";

// Phase 2 refusals that send the manager back to the Affilizz verification.
export const LAUNCH_REJECTION_CODES = [
  "affilizz_not_affiliated",
  "affilizz_affiliation_not_confirmed",
  "ctc_selection_changed",
];

// Shown while a request runs; the browser receives no real-time phase.
export const VERIFICATION_OPERATIONS = [
  "Création du bouton d’offre (CTA) du produit principal",
  "Création du widget comparatif, s’il est inclus",
  "Lecture de l’affiliation de chaque contenu",
];
export const LAUNCH_PROGRESS_OPERATIONS = [
  "Enregistrement des paramètres de l’article",
  "Réutilisation des contenus Affilizz vérifiés",
  "Transmission à la génération",
];

// Affilizz `affiliated`: true, false, or null while Affilizz still checks.
export const AFFILIATION_LABELS = {
  affiliated: "Affilié",
  not_affiliated: "Non affilié",
  pending: "Vérification en cours",
};
// The site setting was saved, but its pending verification stayed.
export const SITE_UPDATED_CANCEL_FAILED_MESSAGE =
  "Le site a été mis à jour, mais la vérification Affilizz n’a pas pu être abandonnée. Elle a été conservée : reprenez-la pour réessayer ou l’abandonner manuellement.";

export const WIDGET_DISABLED_LABEL = "Désactivé";
// Badge text of one Affilizz component (componentStateOf); its tones live in
// component-state-badges.js, this file importing no style alias.
export const COMPONENT_STATE_LABELS = {
  ...AFFILIATION_LABELS,
  disabled: WIDGET_DISABLED_LABEL,
};
export const WIDGET_DISABLED_DETAIL =
  "Désactivé — il ne sera pas intégré dans l’article.";
export const AFFILIATION_RECHECK_HELP =
  "Relecture manuelle des mêmes contenus, au plus une fois par minute : elle observe l’état chez Affilizz, elle ne déclenche pas l’affiliation. Affilizz ne publie aucun délai.";

// Blocked hand-overs to AifPostGeneration: what to fix before a new try.
export const GENERATION_BLOCK_LABELS = {
  affilizz_not_affiliated: "Produit non affilié chez Affilizz",
  ctc_template_unavailable: "Template CTC indisponible",
  ctc_schedule_expired: "Date de programmation dépassée",
  aif_recent_duplicate: "Même titre envoyé il y a moins de 5 min",
  affilizz_affiliation_pending: "Affiliation en cours de vérification",
  affilizz_widget_not_affiliated: "Widget non affilié",
  ctc_not_finalized: "Vérification non finalisée",
};

// What each format shows the reader, limited to the payload actually sent
// (report-api ctc_affilizz._widget_content): BOX one tab per model with its
// colours, CARD one flat product list. Nothing else of the Affilizz rendering
// (image, order, highlighted product) is documented, so nothing else is said.
export const WIDGET_FORMATS = [
  {
    value: "BOX",
    name: "Comparateur de prix",
    description: "Un onglet par modèle, avec ses coloris et ses offres.",
  },
  {
    value: "CARD",
    name: "Carte produit",
    description: "Une carte regroupe les produits sélectionnés, sans onglets.",
  },
];

// Same limits as the backend: one BOX tab per model, colours included.
export const WIDGET_MAX_MODELS = 3;
export const WIDGET_MAX_PRODUCTS = 20;

// Time slots offered when a publication is scheduled.
export const SCHEDULE_STEP_MINUTES = 15;

// Requested publication: AifPostGeneration applies it after the generation.
export const REQUESTED_PUBLICATION_LABELS = {
  draft: "Envoi en brouillon demandé après génération",
  publish: "Publication demandée après génération",
  future: "Publication programmée après génération",
};

export const OPPORTUNITY_STATUSES = {
  new: { label: "Nouvelle", variant: "info" },
  used: { label: "Utilisée", variant: "success" },
  ignored: { label: "Ignorée", variant: "neutral" },
};

export const HIGHLIGHT_DURATION = 3000;
